import React from 'react';
import R from 'ramda';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CardoGroup from '@cotyorg/ccx-organism-cardo-group/components/CardoGroup';
import { CardsCarousel } from '@cotyorg/ccx-organism-carousel';
import { HeroFullAsymmetric } from '@cotyorg/ccx-organism-hero';
import Heading from '@cotyorg/ccx-atoms/Heading';
import Link from '@cotyorg/ccx-atoms/Link';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import { trackCarouselClickEvent } from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/carousel';
import PageDescription from '../../organisms/PageDescription';
import StandardLayout from '../../templates/StandardLayout';
import { cardSection1BreakpointConfig } from './cardo.config';

const TrendsComponent = ({
  sectionHeadings,
  cta,
  hero,
  cards,
  cardsSection,
}) => (
  <StandardLayout>
    <div className="trends">
      <div className="main-hero-section">
        <div className="main-hero-heading">
          <PageDescription
            heading={R.path(
              ['introductionSection', 'heading'],
              sectionHeadings
            )}
            text={R.path(
              ['introductionSection', 'subheading'],
              sectionHeadings
            )}
          />
        </div>

        <div className="grid-container">
          <div className="grid-x">
            <div className="cell xsmall-16">
              <HeroFullAsymmetric
                isContentFirst
                ctaType="button"
                alignment="bottom"
                heroStoreDataKey="hero1"
                isUnderlined={false}
                headingHasLink
                imageHasLink
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid-container">
        <div className="grid-x">
          <div className="cell xsmall-16 large-14 large-offset-1">
            <div className="featured-products">
              <div className="sub-heading">
                <PageDescription
                  level={2}
                  heading={R.path(
                    ['productCarouselSection', 'heading'],
                    sectionHeadings
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <OptionalSection
          renderCondition={R.prop('productCarouselSection', cards)}
          section={() => (
            <div className="featured-products">
              <div className="grid-container">
                <div className="grid-x">
                  <div className="cell xsmall-14 xsmall-offset-1 large-14 large-offset-1">
                    <div data-test="cards-carousel">
                      <CardsCarousel
                        cards={{
                          cardsStoreDataKey: 'productCarouselSection',
                          isTitleUnderlined: false,
                          hasBackground: false,
                          isSmallTitle: true,
                          imgRatingOnSrc: {
                            fullStar: '/ccx-files/assets/star-on.svg',
                            partialStar1:
                              '/ccx-files/assets/partial-star-1.svg',
                            partialStar2:
                              '/ccx-files/assets/partial-star-2.svg',
                            partialStar3:
                              '/ccx-files/assets/partial-star-3.svg',
                            partialStar4:
                              '/ccx-files/assets/partial-star-4.svg',
                            partialStar5:
                              '/ccx-files/assets/partial-star-5.svg',
                            partialStar6:
                              '/ccx-files/assets/partial-star-6.svg',
                            partialStar7:
                              '/ccx-files/assets/partial-star-7.svg',
                            partialStar8:
                              '/ccx-files/assets/partial-star-8.svg',
                            partialStar9:
                              '/ccx-files/assets/partial-star-9.svg',
                          },
                          imgRatingOffSrc: '/ccx-files/assets/star-off.svg',
                          zoomOnHover: true,
                          fixedHeight: false,
                          showStarRating: true,
                        }}
                        carousel={{
                          carouselsStoreDataKey: 'productCarouselSection',
                          headingLevel: 3,
                          rightChevronIconSrc:
                            '/ccx-files/assets/chevron-right.svg',
                          leftChevronIconSrc:
                            '/ccx-files/assets/chevron-left.svg',
                          onIndexChange: ({ type, index, isForward }) =>
                            trackCarouselClickEvent({
                              page: 'trends',
                              carousel: 'productCarouselSection',
                              type,
                              index,
                              isForward,
                            }),
                        }}
                        cardsPerPageMap={{
                          xsmall: 1,
                          small: 2,
                          medium: 3,
                          large: 4,
                          xlarge: 4,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        />
      </div>

      <OptionalSection
        renderCondition={!!R.length(cardsSection)}
        section={() => (
          <div className="cardo-buttons-cta">
            <div className="grid-container">
              <div className="grid-x">
                <div className="cell xsmall-16 large-14 large-offset-1">
                  <div className="grid-x grid-padding-x flex-container align-center">
                    <CardoGroup
                      data="cardsSection"
                      baseBreakpointConfig={
                        cardSection1BreakpointConfig.baseBreakpointConfig
                      }
                      everyNthBreakpointConfig={
                        cardSection1BreakpointConfig.everyNthBreakpointConfig
                      }
                      headingLevel={3}
                      extraClass="feat-products"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      />

      <OptionalSection
        renderCondition={R.prop('url', cta) && R.prop('title', cta)}
        section={() => (
          <div className="grid-container">
            <div className="grid-x">
              <div className="cell xsmall-16 large-14 large-offset-1">
                <div className="cta trends-cta">
                  <Link
                    title={R.prop('title', cta)}
                    link={R.prop('url', cta)}
                    extraClass="button button--large button--primary alltrends__button"
                  >
                    {R.prop('title', cta)}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      />

      <OptionalSection
        renderCondition={R.prop('hero2', hero)}
        section={() => (
          <div className="main-hero-section trends-hero">
            <div className="grid-container">
              <div className="grid-x">
                <div className="cell xsmall-16">
                  <HeroFullAsymmetric
                    isContentFirst
                    alignment="bottom"
                    ctaType="button"
                    heroStoreDataKey="hero2"
                    isUnderlined={false}
                    headingHasLink
                    imageHasLink
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      />
    </div>
  </StandardLayout>
);

TrendsComponent.propTypes = {
  sectionHeadings: PropTypes.objectOf(PropTypes.string),
  cta: PropTypes.objectOf(PropTypes.string),
  hero: PropTypes.objectOf(PropTypes.object),
  cards: PropTypes.objectOf(PropTypes.object),
  cardsSection: PropTypes.arrayOf(PropTypes.string),
};

export default connect(({ content }) => ({
  sectionHeadings: R.prop('sectionHeadings', content),
  cta: R.prop('cta', content),
  hero: R.prop('hero', content),
  cards: R.prop('cards', content),
  cardsSection: R.path(['cardoGroup', 'cardsSection'], content),
}))(TrendsComponent);
